import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Slider } from '@blueprintjs/core';
import { changeNoiseLevel, selectNoiseLevel } from '../classState/studentsSlice';
import './VolumeSlider.css';

/**
 * VolumeSlider Component
 *
 * This component renders a slider that allows users to adjust the volume level in class.
 * The slider displays values between 0 and 10, where 0 represents "Quiet" and
 * 10 represents "Loud". Internally, the slider operates with a step size of 0.05,
 * allowing for finer granularity in volume adjustment, but the displayed labels
 * round the values to whole numbers for a user-friendly experience.
 *
 * Props:
 * - None
 *
 * State (via Redux):
 * - level (number): The current noise level, selected from the global state using `selectNoiseLevel`.
 *
 * Methods:
 * - handleSliderChange(value): Dispatches the new noise level to the Redux store,
 *   allowing the state to update accordingly. The value passed is in increments of 0.05.
 *
 * Example:
 * <VolumeSlider />
 */
const VolumeSlider = () => {
    const dispatch = useDispatch();
    const level = useSelector(selectNoiseLevel);
    const displayedLevel = level * 10;

    const roundToStep = (value, step) => {
        return Math.round(value / step) * step;
    };

    const handleSliderChange = (value) => {
        const scaledValue = roundToStep(value / 10, 0.05);
        dispatch(changeNoiseLevel(scaledValue.toFixed(2)));
    };

    return (
        <div className="volume-slider">
            <Slider
                min={0}
                max={10}
                stepSize={0.05}
                labelStepSize={10}
                value={displayedLevel} // Displayed value between 0-10
                onChange={handleSliderChange}
                className="custom-slider"
                labelRenderer={(val) => Math.round(val)} // Round labels to whole numbers
            />
        </div>
    );
};

export default VolumeSlider
